import { IOptionItem, IOrder, IThemeColors, ITrackingItem } from '@wyz/types';
import { SUPPLIERS_TYPES_IDS } from '@wyz/utils';
import { IDistinctOrderSupplierResponse } from '~/purchase/types';
import { IconProps } from '@wyz/ui';
import { SERVICE_EAN } from '~/purchase/helpers';

export const flattenSuppliers = (
  suppliers: Array<IDistinctOrderSupplierResponse>,
): Array<IOptionItem<number> & { targetIds: Array<number> }> => {
  if (!suppliers.length) return [];

  const all: Array<IOptionItem<number> & { targetIds: Array<number> }> = [];
  const wyzDistributorIds: Array<number> = [];
  let wyzDistributorName: string | undefined = undefined;
  suppliers.forEach((supplier, index) => {
    if (supplier.supplierTypeId === SUPPLIERS_TYPES_IDS.WYZ_DISTRIBUTOR) {
      wyzDistributorIds.push(supplier.supplierId);
      wyzDistributorName = supplier.supplierName;
    } else {
      all.push({
        value: index,
        label: supplier.supplierName,
        targetIds: [supplier.supplierId],
      });
    }
  });

  if (wyzDistributorName && wyzDistributorIds.length > 0) {
    all.push({
      value: -1,
      label: wyzDistributorName,
      targetIds: wyzDistributorIds,
    });
  }

  return all;
};

const getHandler = (defaultValue: string) => ({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get(target: object, propertyKey: PropertyKey, receiver?: any) {
    const value = Reflect.get(target, propertyKey, receiver);
    if (value === undefined) return defaultValue;
    return value;
  },
});

export const TRACKING_STATUS_COLOR_LOOKUP = new Proxy<
  Record<ITrackingItem['status'], IThemeColors>
>(
  {
    DELIVERED_TO_RECIPIENT: 'success',
    IN_TRANSIT_SHIPPING: 'warning',
    DROPPED_AT_PICKUP_POINT: 'info',
    OUT_FOR_DELIVERY: 'warning',
    DELIVERY_INTERRUPTED: 'danger',
    PICKED_UP_BY_CARRIER: 'info',
    DELIVERY_RESCHEDULED: 'warning',
    LOCATION_CLOSED: 'danger',
    ADDRESS_ISSUE: 'danger',
    PICKED_UP_BY_RECIPIENT: 'success',
    DELIVERED_TO_THIRD_PARTY: 'success',
    DELIVERED: 'success',
    DELIVERY_INCIDENT: 'danger',
    IN_TRANSIT: 'warning',
    READY_TO_SHIP: 'info',
    DELIVERY_SCHEDULED_TODAY: 'info',
    PENDING: 'warning',
    DELIVERY_FAILED: 'danger',
    REFUSED_RETURNED: 'danger',
    AWAITING_CARRIER_INFO: 'warning',
    TRACKING_CREATED: 'info',
    TRACKING_EXPIRED: 'danger',
    AVAILABLE_AT_RELAY_POINT: 'info',
  },
  getHandler('default'),
);

export const ORDER_STATUS_COLOR_LOOKUP = new Proxy<
  Record<number, IThemeColors>
>(
  {
    1: 'warning',
    2: 'warning',
    3: 'warning',
    4: 'success',
    5: 'danger',
    6: 'danger',
    10: 'warning', // STATUS_WAITING
    15: 'warning', // STATUS_SEND_SUPPLIER
    20: 'warning', // STATUS_APPROVED
    30: 'warning', // STATUS_DELIVERY_PROGRESS
    35: 'warning', // STATUS_DELIVERY_DAY
    40: 'success', // STATUS_DELIVERED
    45: 'success', // STATUS_FINISHED
    50: 'danger', // STATUS_CANCELED_SUPPLIER
    51: 'warning', // STATUS_BREAK_SUPPLIER
    60: 'danger', // STATUS_CANCELED_CLIENT
    61: 'danger', // STATUS_CANCELED_BUYER
    62: 'danger', // STATUS_CANCELED_BANK
    70: 'danger', // STATUS_CANCELED_WYZ
  },
  getHandler('default'),
);

// Note: we only put what we want to override
export const PROCESSED_ORDER_STATUS_COLOR_LOOKUP: Record<number, IThemeColors> =
  {
    ...ORDER_STATUS_COLOR_LOOKUP,
    20: 'success', // STATUS_APPROVED
  };

export function getServiceIcon(
  order: IOrder,
  serviceEan: string,
): IconProps['icon'] {
  const lookup: Record<string, IconProps['icon']> = {
    [SERVICE_EAN.J0_DELIVERY]: order.todayDelivery
      ? 'today-delivery'
      : 'next-day-delivery',
    [SERVICE_EAN.ONE_TYRE_DELIVERY]: 'football-fill',
  };

  return lookup[serviceEan] ?? 'football-fill';
}
